import ReactDOM from "react-dom/client";
import { BrowserRouter, useNavigate } from "react-router-dom";
import { StyleSheetManager } from "styled-components";
import isPropValid from "@emotion/is-prop-valid";
import {
  AuthUserProvider,
  getAuthConfig,
  getAuthConfigByKey,
  SessionTimeoutProvider,
  useAuth,
  useUser,
  VITE_MODE_MAPPING,
  type LogoutReason,
} from "@webuild-ai/react-auth";
import React, { useCallback } from "react";

import App from "./App.tsx";
import { DeferredSessionWarningModal } from "./components/Session/DeferredSessionWarningModal.tsx";
import i18n, { REACT_AUTH_NAMESPACE, REACT_UI_NAMESPACE } from "./i18n";
// Load Iconify configuration to prevent CDN/CORS issues
import "./iconify-config";
import { HeroProvider } from "./providers/HeroProvider.tsx";
import "@/styles/globals.css";
import { WeBuildClientProvider } from "./providers/WeBuildClientProvider.tsx";
import { RBACProviderWrapper } from "./providers/RBACProviderWrapper.tsx";
import { EmbeddedAuthProvider, useEmbeddedAuthConfig } from "./providers/EmbeddedAuthProvider.tsx";
import { useIdpSessionHandler } from "./hooks/useIdpSessionHandler.ts";
import { useAppLogout } from "./hooks/useAppLogout.ts";
import { useSessionDebug } from "./hooks/useSessionDebug.ts";
import { ThemeProvider } from "./hooks/use-theme.tsx";
import { TenantProvider } from "./providers/TenantProvider.tsx";
import { resolvePathwayTenantKey } from "./config/tenants.ts";
import ErrorBoundary from "./services/ErrorBoundaries.tsx";
import { TelemetryProvider, logger } from "./telemetry";

// HeroUI's toast queue wraps every update in `document.startViewTransition()` and
// never attaches a `.catch()` to the returned `transition.finished` promise. When two
// toasts are added in quick succession (e.g. "Exporting…" → "Exported successfully" on a
// fast Excel/PDF/Word export) the second transition is aborted and the rejection bubbles
// up as an unhandled `DOMException: Skipped ViewTransition…`. The DOM still updates via
// the synchronous fallback, so this is purely console noise — swallow only that exact
// rejection and let everything else propagate.
// Chromium emits several distinct DOMException messages when a view transition aborts:
//   - "Skipped ViewTransition due to another transition starting" (older)
//   - "Transition was skipped"                                    (newer)
//   - "Transition was aborted because of invalid state"           (mid-flight nav / DOM swap)
// All are benign in the context of HeroUI's toast queue (which wraps every update in
// `document.startViewTransition()` without `.catch()`-ing the rejection). Match the family.
const VIEW_TRANSITION_NOISE_RE = /ViewTransition|Transition was (skipped|aborted)/i;

window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason as unknown;

  if (reason instanceof DOMException && VIEW_TRANSITION_NOISE_RE.test(reason.message)) {
    event.preventDefault();
  }
});

// Resolve the initial tenant key from the auth config. First try the explicit
// Vite mode mapping, then fall back to hostname-based detection. The key is
// resolved through pathway's tenant aliases so the WeBuild Azure AD dev tenant
// (an onmicrosoft.com domain) maps to the WeBuild pack rather than the neutral
// fallback. The tenant is refined per-user after authentication in App.tsx.
const mode = import.meta.env.MODE as string;
const configKey = VITE_MODE_MAPPING[mode];
const initialAuthConfig = configKey ? getAuthConfigByKey(configKey) : getAuthConfig();
const initialTenantKey = resolvePathwayTenantKey(initialAuthConfig?.tenant ?? "webuild-ai.com");

// Wrapper component to provide navigation to AuthUserProvider
const AuthProviderWithNavigation: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const mode = import.meta.env.MODE as string;

  // Embedded (iframe) SSO config from the parent-window handshake. Inert when standalone.
  const { isEmbedded, loginHint, externalToken, onInteractionRequired, onAuthEvent } =
    useEmbeddedAuthConfig();

  // Get auth config using explicit mode mapping, fall back to hostname-based detection
  // This allows a single build to work across multiple deployment targets
  const configKey = VITE_MODE_MAPPING[mode];
  const authConfig = configKey ? getAuthConfigByKey(configKey) : getAuthConfig();

  // Simple fallback if no config found (should not happen with hostname detection)
  if (!authConfig) {
    logger.error(`No auth config found for mode: ${mode}`, undefined, {
      mode,
      configKey: configKey ?? "undefined",
    });

    return <div>Configuration error for mode: {mode}</div>;
  }

  return (
    <AuthUserProvider
      authConfig={authConfig}
      embedded={isEmbedded}
      externalToken={externalToken}
      loginHint={loginHint}
      onAuthEvent={onAuthEvent}
      onInteractionRequired={onInteractionRequired}
      onRedirectCallback={(path) => navigate(path)}
    >
      {children}
    </AuthUserProvider>
  );
};

/**
 * Session Timeout Wrapper - Wraps SessionTimeoutProvider with required callbacks.
 *
 * This component:
 * - Provides the logout callback for session expiry
 * - Handles IdP session expiration events from WeBuildClientProvider
 * - Performs hard logout when session expires
 *
 * NOTE: SessionTimeoutProvider automatically detects auth state and prevents MSAL
 * `interaction_in_progress` errors. The `useIdpSessionHandler` hook still requires
 * manual auth guards since it's Pathway-specific. From react-auth v4 the
 * `WarningModal` prop is required; pathway supplies react-ui's `SessionWarningModal`.
 */
const SessionTimeoutWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoading, isAuthenticated } = useAuth();
  const { userProfile } = useUser();
  const appLogout = useAppLogout();

  // Use i18n.t directly to translate the injected SessionWarningModal. The modal
  // strings (session.modal.*) live in the reactUi namespace from v4; auth strings
  // remain in reactAuth, so search both. This avoids the useTranslation hook,
  // which can cause issues during auth redirect flows.
  const t = useCallback(
    (key: string) => i18n.t(key, { ns: [REACT_UI_NAMESPACE, REACT_AUTH_NAMESPACE] }),
    [],
  );

  // Guard: Don't enable session timeout management until auth is fully initialised
  // This prevents the MSAL `interaction_in_progress` error during redirect flows
  const sessionEnabled = !isLoading && isAuthenticated;

  // Handle IdP session expiration events from WeBuildClientProvider
  // Only set up the handler when authenticated to avoid race conditions
  useIdpSessionHandler({ enabled: sessionEnabled });

  // Get tenant from user profile
  const tenant = userProfile?.tenant || "default";

  // Diagnostic for session expiry, active only when VITE_SESSION_DEBUG=true in dev.
  useSessionDebug({ isLoading, isAuthenticated, tenant });

  /**
   * Handle session expiry by performing hard logout.
   */
  const handleSessionExpired = useCallback(
    (reason: "absolute" | "idle" | "idp_revoked" | "manual") => {
      const logoutReason: LogoutReason =
        reason === "absolute"
          ? "session_expired"
          : reason === "idle"
            ? "idle_timeout"
            : reason === "manual"
              ? "user_requested"
              : "idp_revoked";

      // useAppLogout owns the audit event, the cache clears and the provider-specific
      // sign-out, so every logout path in Pathway behaves identically.
      appLogout(logoutReason);
    },
    [appLogout],
  );

  return (
    <SessionTimeoutProvider
      WarningModal={DeferredSessionWarningModal}
      t={t}
      tenantKey={tenant}
      onSessionExpired={handleSessionExpired}
    >
      {children}
    </SessionTimeoutProvider>
  );
};

ReactDOM.createRoot(document.getElementById("root")!).render(
  // <React.StrictMode>
  <ErrorBoundary>
    <TenantProvider initialTenantKey={initialTenantKey}>
      <ThemeProvider>
        <BrowserRouter basename={import.meta.env.BASE_URL}>
          <HeroProvider>
            <EmbeddedAuthProvider>
              <AuthProviderWithNavigation>
                <TelemetryProvider>
                  <WeBuildClientProvider>
                    <RBACProviderWrapper>
                      <SessionTimeoutWrapper>
                        <StyleSheetManager
                          //required for styled-components to work
                          shouldForwardProp={(prop, _) => isPropValid(prop)}
                        >
                          <App />
                        </StyleSheetManager>
                      </SessionTimeoutWrapper>
                    </RBACProviderWrapper>
                  </WeBuildClientProvider>
                </TelemetryProvider>
              </AuthProviderWithNavigation>
            </EmbeddedAuthProvider>
          </HeroProvider>
        </BrowserRouter>
      </ThemeProvider>
    </TenantProvider>
  </ErrorBoundary>,
  // </React.StrictMode>,
);
